import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Component, OnInit, AfterViewInit, ViewChildren, QueryList } from '@angular/core';
import { NgSelectComponent } from '@ng-select/ng-select';
import { RolesStudyService } from '../../services/roles-study.service';
import { ToastrService } from 'ngx-toastr';
import { PLATFORM_SELECTION_ERROR_MSG, ROLE_SELECTION_ERROR_MSG, SESSION_EXPIRED_MSG, STUDY_SELECTION_ERROR_MSG } from '../../constant/responseMessage';
import { AuditlogService } from '../../services/auditlog.service';
import  { CookieService } from 'ngx-cookie-service';
import { EDC_PLATFORM, EDC_PORT, envName, ETMF_PLATFORM, ETMF_PORT, LOCAL_PROFILE, PLATFORM, ROLE, STUDY, TSM_PLATFORM, TSM_PORT, CTMS_PLATFORM, CTMS_PORT, MAX_SEARCH_INPUT_LENGTH, COOKIE_KEYS, IS_LOCAL_TO_SERVER, CENTRAL_PLATFORM, IS_NGINX, IWRS_PLATFORM, IWRS_PORT } from 'src/app/constant/constant';
import { LoginService } from 'src/app/services/login.service';
import type { PlatformStudyRoleDTO, PlatformRoleDTO, PlatformStudyDTO, LoginPayloadDTO, RolesStudyResponseDTO } from '../dtos/PlatformStudyRoleDTO';
import { RoleAccessLevel } from '../role/role.constant';
import { MultiSelectSimpleConfig } from 'src/app/interface/multiSelectConfig';
import { NavigationEnd, Router } from '@angular/router';
import { AuthGuardService } from 'src/app/services/auth-guard.service';
import { filter } from 'rxjs/operators';
import { HttpStatusCode } from '@angular/common/http';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ChangePasswordComponent } from '../change-password/change-password.component';

@Component({
  selector: 'app-roles-and-study',
  templateUrl: './roles-and-study.component.html',
  styleUrls: ['./roles-and-study.component.css']
})
export class RolesAndStudyComponent implements OnInit, AfterViewInit {

  PLATFORM_PORT_MAP: Record<string, number> = {
        [EDC_PLATFORM]: EDC_PORT,
        [ETMF_PLATFORM]: ETMF_PORT,
        [TSM_PLATFORM]: TSM_PORT,
        [CTMS_PLATFORM]: CTMS_PORT,
        [IWRS_PLATFORM]: IWRS_PORT
      };
  
  @ViewChildren(NgSelectComponent) ngSelectComponents!: QueryList<NgSelectComponent>;
  
  platformStudyRoleDTO: PlatformStudyRoleDTO[] = [];

  availableRoles: PlatformRoleDTO[] = [];

  availableStudies: PlatformStudyDTO[] = [];

  isSystemLevelRole: boolean = false;
  selectedAccessLevel: RoleAccessLevel | null = null;

  formValue: FormGroup;
  loading: boolean = false;

  studyNotSelected: string = '';
  roleNotSelected: string = '';
  platformNotSelected: string = '';
  study = STUDY;
  role = ROLE;
  platform = PLATFORM;

  isRoleAPICall: boolean = false;
  isCancel: boolean = false;

  constructor(
    private rolesStudy: RolesStudyService,
    private toastr: ToastrService,
    private router: Router,
    private cookieService: CookieService,
    private auditLogService: AuditlogService,
    private loginAPI: LoginService,
    private authGuardService: AuthGuardService,
    private modalService: NgbModal,
  ) {
    this.formValue = new FormGroup({
      platform: new FormControl(null, [Validators.required]),
      role: new FormControl({ value: null, disabled: true }, [Validators.required]),
      study: new FormControl({ value: null, disabled: true }, [Validators.required]),
    });
  }

  ngOnInit(): void {
    if (this.cookieService.get(COOKIE_KEYS.USER_ID)) {
      this.isRoleAPICall = true;
      this.getData();
    }

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        if (!this.isRoleAPICall && !this.isCancel) {
          this.isRoleAPICall = true;
          this.getData();
        }
      });
  }

  ngAfterViewInit(): void {
    this.ngSelectComponents.changes.subscribe(() => {
      this.setMaxLengthOnSearchInputs();
    });
    this.setMaxLengthOnSearchInputs();
  }

  private setMaxLengthOnSearchInputs(): void {
    this.ngSelectComponents.forEach((ngSelect: NgSelectComponent) => {
      const searchInput = ngSelect.searchInput?.nativeElement;
      if (searchInput) {
        searchInput.setAttribute('maxlength', String(MAX_SEARCH_INPUT_LENGTH));
      }
    });
  }

  getData(): void {
    this.rolesStudy.getRolesStudy().subscribe({
      next: (data: RolesStudyResponseDTO) => {
        this.platformStudyRoleDTO = data.platforms;
        this.checkPasswordChanged(data);
        // Auto-select platform if only one exists
        if (this.platformStudyRoleDTO.length === 1) {
          const only = this.platformStudyRoleDTO[0];
          this.formValue.patchValue({ platform: only.platform });
          this.onPlatformItemSelect(only);
        }
      },
      error: (error) => {
        if (error.status !== HttpStatusCode.Unauthorized) {
          this.cancelLogin();
        }
      }
    });
  }

  /** Force the change-password popup when the response reports passwordChanged = false */
  private checkPasswordChanged(response: RolesStudyResponseDTO): void {
    if (response.passwordChanged === false && !this.modalService.hasOpenModals()) {
      const modal = this.modalService.open(ChangePasswordComponent, { size: 'md', backdrop: 'static', keyboard: false });
      modal.componentInstance.fromLogin = true;
    }
  }

  /** Called when user selects a platform */
  onPlatformItemSelect(event: PlatformStudyRoleDTO): void {
    const selectedPlatform = this.platformStudyRoleDTO.find(p => p.platform === event?.platform);

    // Reset downstream selections
    this.availableRoles = [];
    this.availableStudies = [];
    this.isSystemLevelRole = false;
    this.selectedAccessLevel = null;

    this.formValue.patchValue({ role: null, study: null });
    this.formValue.get(ROLE)?.disable();
    this.formValue.get(STUDY)?.disable();

    if (selectedPlatform) {
      this.availableRoles = selectedPlatform.roles;
      this.formValue.get(ROLE)?.enable();

      // Auto-select role if only one exists
      if (this.availableRoles.length === 1) {
        const only = this.availableRoles[0];
        this.formValue.patchValue({ role: only.roleId });
        this.onRoleItemSelect(only);
      }
    }
  }

  /** Called when user selects a role */
  onRoleItemSelect(event: PlatformRoleDTO | null): void {
    // Reset study
    this.availableStudies = [];
    this.isSystemLevelRole = false;
    this.selectedAccessLevel = null;
    this.formValue.patchValue({ study: null });
    this.formValue.get(STUDY)?.disable();

    if (!event) return;

    const selectedRole = this.availableRoles.find(r => r.roleId === event.roleId);
    if (!selectedRole) return;

    this.selectedAccessLevel = selectedRole.accessLevel;

    if (selectedRole.accessLevel === RoleAccessLevel.SYSTEM) {
      // System-level: no study selection needed
      this.isSystemLevelRole = true;
    } else {
      // Study-level: populate studies and enable study dropdown
      this.availableStudies = selectedRole.studies || [];
      this.formValue.get(STUDY)?.enable();

      // Auto-select study if only one exists
      if (this.availableStudies.length === 1) {
        this.formValue.patchValue({ study: this.availableStudies[0].studyId });
      }
    }
  }

  onSelectionChange(): void {
    this.studyNotSelected = '';
    this.roleNotSelected = '';
    this.platformNotSelected = '';
  }

  validateLoginCredentials(): boolean {
    let isValid = true;
    this.platformNotSelected = '';
    this.studyNotSelected = '';
    this.roleNotSelected = '';

    if (this.formValue.controls[PLATFORM].hasError('required')) {
      this.platformNotSelected = PLATFORM_SELECTION_ERROR_MSG;
      isValid = false;
    }

    if (this.formValue.controls[ROLE].hasError('required')) {
      this.roleNotSelected = ROLE_SELECTION_ERROR_MSG;
      isValid = false;
    }

    // Only validate study for non-system roles
    if (!this.isSystemLevelRole && this.formValue.controls[STUDY].hasError('required')) {
      this.studyNotSelected = STUDY_SELECTION_ERROR_MSG;
      isValid = false;
    }

    return isValid;
  }

  submitPlatformStudySite(): void {
    if (!this.validateLoginCredentials()) return;

    this.loading = true;

    const selectedRoleId: number = this.formValue.value.role;
    const selectedStudyId: string | null = this.formValue.value.study;

    // Find selected role/study details
    const selectedRole = this.availableRoles.find(r => r.roleId === selectedRoleId) ?? null;
    const selectedStudy = this.availableStudies.find(s => s.studyId === selectedStudyId) ?? null;

    const payload: LoginPayloadDTO = {
      studyId: this.isSystemLevelRole ? null : (selectedStudy?.studyId ?? null),
      studyName: this.isSystemLevelRole ? null : (selectedStudy?.studyName ?? null),
      role: String(selectedRoleId),
      roleName: selectedRole?.roleName ?? null,
      platform: this.formValue.value.platform,
      accessLevel: this.selectedAccessLevel!,
    };

    const isLocal: boolean = envName === LOCAL_PROFILE;
    const redirectionUrl: string = ((isLocal || IS_LOCAL_TO_SERVER) && !IS_NGINX)
      ? `${isLocal ? 'http' : 'https'}://${window.location.hostname}:${this.PLATFORM_PORT_MAP[payload.platform]}/index.html`
      : `${window.location.origin}/${payload.platform}/index.html`;

    this.postLoginAuditLog(payload, redirectionUrl);
  }

  postLoginAuditLog = (payload: LoginPayloadDTO, redirectionUrl: string): void => {
    this.loading = true;

    this.auditLogService.postLoggedInLog(payload).subscribe({
      next: () => {
        if (this.formValue.value.platform === CENTRAL_PLATFORM) {
          // Full navigation, not router.navigate(): the ROLE cookie just set by this
          // request's (cross-origin, api. subdomain) response isn't reliably visible to
          // document.cookie until a real page load, which is what AuthGuardService reads
          // when it gates the /CENTRAL routes.
          window.location.replace(`${window.location.origin}/${CENTRAL_PLATFORM}/`);
        } else {
          window.location.replace(redirectionUrl);
        }
      },
      error: (error) => {
        if (error.status == HttpStatusCode.Unauthorized) {
          this.toastr.info(SESSION_EXPIRED_MSG);
          // The auth interceptor automatically handles logout for 401s
        } else {
          this.cancelLogin();
        }
      }
    }).add(() => this.loading = false);
  };

  cancelLogin(): void {
    this.loading = true;
    // no platform chosen yet: don't send the platform header
    this.loginAPI.logout(true).subscribe((res: { status: number }) => {
      if (res.status == 0) {
        this.isCancel = true;
        this.authGuardService.logout();
      }
    });
  }

  configureRoleStudySelectionBox(): MultiSelectSimpleConfig[] {
    const isRoleDisabled: boolean = this.formValue.get(ROLE)?.disabled ?? true;
    const isStudyDisabled: boolean = this.formValue.get(STUDY)?.disabled ?? true;

    const configs: MultiSelectSimpleConfig[] = [
      {
        multiSelectData: this.platformStudyRoleDTO,
        showLabel: PLATFORM,
        getSelectedValue: PLATFORM,
        multiSelectPlaceholder: 'Select Platform',
        itemsShowLimit: 1,
        formNameControlData: PLATFORM,
        isShowDropDown: true,
        isCloseOnSelect: true,
        isClearable: false,
        isSearchable: true,
        isMultiple: false,
        isSelectAll: false,
        isReadOnly: false,
        isHideOnSelect: false,
        isVirtualScroll: false,
        required: false,
        onApplyMethod: false,
        testid: 'platform',
      },
      {
        multiSelectData: this.availableRoles,
        showLabel: 'roleName',
        getSelectedValue: 'roleId',
        multiSelectPlaceholder: 'Select Role',
        itemsShowLimit: 1,
        formNameControlData: ROLE,
        isShowDropDown: true,
        isCloseOnSelect: true,
        isClearable: false,
        isSearchable: !isRoleDisabled,
        isMultiple: false,
        isSelectAll: false,
        isReadOnly: false,
        isHideOnSelect: false,
        isVirtualScroll: false,
        required: false,
        onApplyMethod: false,
        testid: 'role',
      },
    ];

    // Only include Study dropdown if the selected role is NOT system-level
    if (!this.isSystemLevelRole) {
      configs.push({
        multiSelectData: this.availableStudies,
        showLabel: 'studyName',
        getSelectedValue: 'studyId',
        multiSelectPlaceholder: 'Select Study',
        itemsShowLimit: 1,
        formNameControlData: STUDY,
        isShowDropDown: true,
        isCloseOnSelect: true,
        isClearable: false,
        isSearchable: !isStudyDisabled,
        isMultiple: false,
        isSelectAll: false,
        isReadOnly: false,
        isHideOnSelect: false,
        isVirtualScroll: false,
        required: false,
        onApplyMethod: true,
        testid: 'study',
      });
    }

    return configs;
  }
}
